import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Car, Phone, User, CheckCircle, ChevronDown, UserPlus, Send, DollarSign, Users, Shield, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const vehicleTypes = [
  'Sedan',
  'SUV',
  'Hatchback',
  'Minivan',
  'Motorcycle',
  'Tuk-Tuk / Bajaj',
  'Bus / Minibus',
  'Other',
];

const BaRidePage = () => {
  const [form, setForm] = useState({
    driver_name: '',
    phone: '',
    vehicle_type: '',
    city: 'Lusaka',
    country: 'Zambia',
    is_referred: false,
    referrer_name: '',
  });
  const [referralForm, setReferralForm] = useState({
    friend_name: '',
    friend_phone: '',
  });
  const [activeSection, setActiveSection] = useState('driver');
  const [submitted, setSubmitted] = useState(false);
  const [referralSubmitted, setReferralSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleReferralChange = (e) => {
    const { name, value } = e.target;
    setReferralForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.driver_name || !form.phone || !form.vehicle_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        driver_name: form.driver_name,
        phone: form.phone,
        vehicle_type: form.vehicle_type,
        city: form.city,
        country: form.country,
        is_referred: form.is_referred,
        referrer_name: form.is_referred ? form.referrer_name : null,
      };
      const res = await fetch(`${API_URL}/api/leads/baride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Interest registered successfully!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReferralSubmit = async (e) => {
    e.preventDefault();
    if (!referralForm.friend_name || !referralForm.friend_phone) {
      toast.error('Please fill in your friend\'s name and phone');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        driver_name: referralForm.friend_name,
        phone: referralForm.friend_phone,
        vehicle_type: 'Pending',
        is_referred: true,
        referrer_name: 'Friend referral',
      };
      const res = await fetch(`${API_URL}/api/leads/baride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setReferralSubmitted(true);
        toast.success('Referral submitted! We\'ll contact your friend.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="baride-page" className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center p-10 rounded-2xl max-w-md mx-4"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(16px)' }}
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
            <CheckCircle size={40} className="text-white" />
          </div>
          <h2 data-testid="baride-success-title" className="text-2xl font-bold text-white mb-3">Thank you for your interest!</h2>
          <p className="text-gray-300 mb-6">We've received your details. Our team will review your information and contact you shortly with everything you need to complete your onboarding.</p>
          <div className="text-sm text-gray-500">You can close this page.</div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="baride-page" className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 80% 50%, #3b82f6 0%, transparent 50%)' }} />
        <div className="max-w-4xl mx-auto px-4 pt-16 pb-6 relative z-10">
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>
              <Car size={18} className="text-amber-400" />
              <span className="text-amber-300 text-sm font-medium">Express Your Interest</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Drive with <span style={{ color: '#f59e0b' }}>BaRide</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              Register your interest and our team will contact you to complete the onboarding process. It's simple, fast, and fair.
            </p>

            {/* Value Proposition */}
            <div className="flex flex-wrap justify-center gap-4 max-w-2xl mx-auto">
              <div className="flex items-center gap-2.5 px-5 py-3 rounded-xl" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <DollarSign size={20} className="text-amber-400" />
                <span className="text-white text-sm font-medium">Keep 97-100% of your earnings</span>
              </div>
              <div className="flex items-center gap-2.5 px-5 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Shield size={20} className="text-amber-400" />
                <span className="text-gray-300 text-sm font-medium">Built for drivers, by drivers</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="max-w-lg mx-auto px-4 mb-4">
        <div className="flex rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            data-testid="baride-tab-driver"
            onClick={() => setActiveSection('driver')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-all ${activeSection === 'driver' ? 'text-white' : 'text-gray-500'}`}
            style={activeSection === 'driver' ? { background: 'rgba(245,158,11,0.2)' } : {}}
          >
            <Car size={16} /> I'm a Driver
          </button>
          <button
            data-testid="baride-tab-referral"
            onClick={() => setActiveSection('referral')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-all ${activeSection === 'referral' ? 'text-white' : 'text-gray-500'}`}
            style={activeSection === 'referral' ? { background: 'rgba(245,158,11,0.2)' } : {}}
          >
            <Users size={16} /> Refer a Friend
          </button>
        </div>
      </div>

      {/* Driver Form Section */}
      {activeSection === 'driver' && (
        <div className="max-w-lg mx-auto px-4 pb-20">
          <motion.form
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            onSubmit={handleSubmit}
            data-testid="baride-form"
            className="rounded-2xl p-8 space-y-6"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)' }}
          >
            <p className="text-gray-400 text-sm">Fill in your details below and we'll get in touch to walk you through the full onboarding.</p>

            {/* Driver Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <User size={14} className="inline mr-1.5 -mt-0.5" />Full Name *
              </label>
              <input
                data-testid="baride-driver-name"
                type="text"
                name="driver_name"
                value={form.driver_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Phone size={14} className="inline mr-1.5 -mt-0.5" />Phone Number *
              </label>
              <input
                data-testid="baride-phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+254 7XX XXX XXX"
                required
                className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>

            {/* Vehicle Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Car size={14} className="inline mr-1.5 -mt-0.5" />Vehicle Type *
              </label>
              <div className="relative">
                <select
                  data-testid="baride-vehicle-type"
                  name="vehicle_type"
                  value={form.vehicle_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl text-white appearance-none outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <option value="" disabled className="bg-slate-800">Select vehicle type</option>
                  {vehicleTypes.map((v) => (
                    <option key={v} value={v} className="bg-slate-800">{v}</option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* City & Country */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <MapPin size={14} className="inline mr-1.5 -mt-0.5" />City *
                </label>
                <input
                  data-testid="baride-city"
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="e.g. Lusaka"
                  required
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <MapPin size={14} className="inline mr-1.5 -mt-0.5" />Country *
                </label>
                <input
                  data-testid="baride-country"
                  type="text"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  placeholder="e.g. Zambia"
                  required
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>
            </div>

            {/* Referral */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  data-testid="baride-is-referred"
                  type="checkbox"
                  name="is_referred"
                  checked={form.is_referred}
                  onChange={handleChange}
                  className="w-5 h-5 rounded accent-amber-500"
                />
                <div>
                  <span className="text-gray-200 text-sm font-medium flex items-center gap-1.5">
                    <UserPlus size={14} className="text-amber-400" /> Were you referred by someone?
                  </span>
                </div>
              </label>
              {form.is_referred && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-4">
                  <input
                    data-testid="baride-referrer-name"
                    type="text"
                    name="referrer_name"
                    value={form.referrer_name}
                    onChange={handleChange}
                    placeholder="Referrer's name"
                    className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </motion.div>
              )}
            </div>

            {/* Submit */}
            <button
              data-testid="baride-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={18} /> Register My Interest
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-500">
              This is an expression of interest. Our team will contact you to complete the onboarding.
            </p>
          </motion.form>
        </div>
      )}

      {/* Refer a Friend Section */}
      {activeSection === 'referral' && (
        <div className="max-w-lg mx-auto px-4 pb-20">
          {referralSubmitted ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center p-8 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)' }}
            >
              <div className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Referral Sent!</h3>
              <p className="text-gray-400 text-sm">Thanks for referring your friend. We'll reach out to them shortly so they can start earning with BaRide.</p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              onSubmit={handleReferralSubmit}
              data-testid="baride-referral-form"
              className="rounded-2xl p-8 space-y-6"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)' }}
            >
              <div className="text-center mb-2">
                <Users size={28} className="text-amber-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-1">Know a driver?</h3>
                <p className="text-gray-400 text-sm">Share their details and we'll contact them directly. Help your friend start earning with BaRide.</p>
              </div>

              {/* Friend Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <User size={14} className="inline mr-1.5 -mt-0.5" />Friend's Full Name *
                </label>
                <input
                  data-testid="baride-friend-name"
                  type="text"
                  name="friend_name"
                  value={referralForm.friend_name}
                  onChange={handleReferralChange}
                  placeholder="Your friend's name"
                  required
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>

              {/* Friend Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Phone size={14} className="inline mr-1.5 -mt-0.5" />Friend's Phone Number *
                </label>
                <input
                  data-testid="baride-friend-phone"
                  type="tel"
                  name="friend_phone"
                  value={referralForm.friend_phone}
                  onChange={handleReferralChange}
                  placeholder="+254 7XX XXX XXX"
                  required
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-amber-500/50"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>

              {/* Submit */}
              <button
                data-testid="baride-referral-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus size={18} /> Refer My Friend
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-500">
                We'll contact your friend to invite them to join BaRide.
              </p>
            </motion.form>
          )}
        </div>
      )}
    </div>
  );
};

export default BaRidePage;
